import { Person, Relationship } from '../../types.ts';
import type { RelatieItem } from './relationships.ts';
import {
  ApiError,
  fetchWithTimeout,
  parseErrorResponse,
  type ZelfMelden,
  BULK_FETCH_TIMEOUT,
  personToServer,
  personFromServer,
  relationshipFromServer,
} from './base.ts';

export const peopleApi = {
  /**
   * @param opts.includeNotes D-03 — vraag ook `notes` en `biography` op. Alleen de
   *   GEDCOM-export heeft die nodig; standaard blijven ze weg omdat biografie rich
   *   text van onbepaalde lengte is en elke lijstweergave dat anders meesleept.
   *   Deze variant gaat bovendien langs de servercache — voor een enkele persoon
   *   is `getPersonDetail` het goedkope pad (#1506).
   */
  getPeople: async (
    projectId: string,
    opts: { includeNotes?: boolean } = {}
  ): Promise<Person[]> => {
    // cache: 'no-store' — Express ETag kan na archief-import een 304 geven
    // ondanks dat de server-side data is gewijzigd, waardoor de personenlijst
    // op /people stale blijft (Refs #912, Nextcloud #551).
    const query = opts.includeNotes ? '?include=notes' : '';
    const res = await fetchWithTimeout(`/api/projects/${projectId}/people${query}`, {
      cache: 'no-store',
    });
    if (!res.ok) await parseErrorResponse(res);
    const rows = await res.json();
    return rows.map(personFromServer);
  },

  getPerson: async (personId: string): Promise<Person> => {
    const res = await fetchWithTimeout(`/api/people/${personId}`);
    if (!res.ok) await parseErrorResponse(res);
    return personFromServer(await res.json());
  },

  /**
   * #1826 — de naam van wie de persoon het laatst wijzigde ná de versie `sinds` (die het
   * formulier kende); null = onbekend, ikzelf (ander tabblad) of niemand.
   */
  getLaatsteBewerker: async (personId: string, sinds?: string): Promise<string | null> => {
    const q = sinds ? `?sinds=${encodeURIComponent(sinds)}` : '';
    const res = await fetchWithTimeout(`/api/people/${personId}/laatste-bewerker${q}`);
    if (!res.ok) return null;
    return ((await res.json()) as { naam?: string | null }).naam || null;
  },

  /**
   * Notitie + biografie van ÉÉN persoon (#1506). De lijst-endpoint draagt die
   * twee kolommen niet, en `?include=notes` op de hele lijst gaat langs de
   * servercache — te duur voor een rapportenpagina die ze maar voor een handvol
   * dossierpersonen nodig heeft. Privacy-redactie gebeurt server-side.
   */
  getPersonDetail: async (
    personId: string
  ): Promise<{ biography?: string | null; notes?: string | null }> => {
    const res = await fetchWithTimeout(`/api/people/${personId}/detail`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * #470: bron-IDs gebruikt bij directe familie (persoon + ouders + kinderen).
   * Server-side SQL join; client-cache mist events per persoon dus doen
   * we dit liever via één endpoint.
   */
  getFamilySourceIds: async (projectId: string, personId: string): Promise<string[]> => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/people/${personId}/family-source-ids`
    );
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.sourceIds) ? data.sourceIds : [];
  },

  savePerson: async (
    person: Partial<Person>,
    // #1825 — clientId = door de client gekozen UUID (idempotente herhaling);
    // relationships = atomair mee opgeslagen. Beide alleen bij een nieuwe persoon.
    opts: { relationships?: RelatieItem[]; clientId?: string } & ZelfMelden = {}
  ): Promise<
    Person & {
      warnings?: Array<{ field: string; message: string; code: string }>;
      relationships?: Relationship[];
      /** POST met clientId: de persoon bestond al (200), de body is genegeerd. */
      bestond?: boolean;
    }
  > => {
    const isUpdate = !!person.id;
    const serverPayload = personToServer(person);

    if (isUpdate) {
      // #1826 — niets te schrijven (de editor stuurt alleen wat de gebruiker wijzigde): geen PUT,
      // die wijst de server af ("No update fields provided"). De stand van nu, zoals na een PUT.
      if (!Object.keys(serverPayload).some((k) => k !== 'expected_version')) {
        const res = await fetchWithTimeout(`/api/people/${person.id}`);
        if (!res.ok) await parseErrorResponse(res, opts);
        return personFromServer(await res.json());
      }
      // Update existing person
      const res = await fetchWithTimeout(`/api/people/${person.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serverPayload),
      });
      if (!res.ok) await parseErrorResponse(res, opts);
      const data = await res.json();
      const saved = personFromServer(data);
      if (data.warnings) (saved as any).warnings = data.warnings;
      return saved;
    } else {
      // Create new person
      const res = await fetchWithTimeout(`/api/projects/${person.projectId}/people`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...serverPayload,
          ...(opts.clientId ? { id: opts.clientId } : {}),
          ...(opts.relationships ? { relationships: opts.relationships } : {}),
        }),
      }).catch((err) => {
        // #1825 — met eigen id is een herhaling veilig; alleen dan die geruststelling.
        if (opts.clientId && err instanceof ApiError && err.code === 'REQUEST_TIMEOUT')
          err.messageKey = 'error_request_timeout_person';
        throw err;
      });
      if (!res.ok) await parseErrorResponse(res, opts);
      const data = await res.json();
      const saved = personFromServer(data);
      if (data.warnings) (saved as any).warnings = data.warnings;
      if (Array.isArray(data.relationships)) {
        (saved as any).relationships = data.relationships.map(relationshipFromServer);
      }
      // #1825 — 200 i.p.v. 201: dit id bestond al (herhaling na een time-out); de server
      // negeerde de body. dataSync legt dan de laatste invoer alsnog vast.
      if (opts.clientId && res.status === 200) (saved as any).bestond = true;
      return saved;
    }
  },

  deletePerson: async (personId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/people/${personId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  bulkDeletePeople: async (
    projectId: string,
    ids: string[]
  ): Promise<{ deleted: number; skippedProbandId: string | null }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/people/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  bulkUpdatePeople: async (
    projectId: string,
    personIds: string[],
    updates: Record<string, string>
  ): Promise<{ updated: number }> => {
    // Convert camelCase field names to snake_case for the database
    const fieldMap: Record<string, string> = {
      givenNames: 'given_names',
      surnamePrefix: 'surname_prefix',
      surname: 'surname',
      gender: 'gender',
      birthDate: 'birth_date',
      birthPlace: 'birth_place',
      deathDate: 'death_date',
      deathPlace: 'death_place',
      occupation: 'occupation',
      religion: 'religion',
    };
    const snakeUpdates: Record<string, string> = {};
    for (const [key, val] of Object.entries(updates)) {
      snakeUpdates[fieldMap[key] || key] = val;
    }
    const res = await fetchWithTimeout(`/api/projects/${projectId}/people/bulk-update`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ personIds, updates: snakeUpdates }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  mergePeople: async (
    projectId: string,
    keepId: string,
    removeId: string,
    mergedData: Record<string, any>
  ): Promise<Person> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/people/merge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ keepId, removeId, mergedData }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return personFromServer(await res.json());
  },

  bulkCreatePeople: async (
    projectId: string,
    people: any[]
  ): Promise<{ id: string; gedcomId: string }[]> => {
    const serverPeople = people.map((p) => personToServer(p));
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/people/bulk`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          people: serverPeople.map((sp, i) => ({
            ...sp,
            gedcomId: people[i].gedcomId || people[i].gedId,
          })),
        }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return data.people;
  },

  getPersonMedia: async (
    projectId: string,
    personId: string
  ): Promise<
    {
      id: string;
      mediaId: string;
      filename: string;
      filePath: string;
      mimeType: string;
      caption: string;
      fileSize: number;
    }[]
  > => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/entity-media/person/${personId}`
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  async getStatisticsTimeline(projectId: string): Promise<{
    birthsByDecade: Array<{ decade: number; count: number }>;
    deathsByDecade: Array<{ decade: number; count: number }>;
  }> {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/statistics/timeline`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  async getStatisticsDemographics(projectId: string): Promise<Record<string, unknown>> {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/statistics/demographics`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
